// The stored enums a `runners` row is keyed by: what a box IS DOING
// (`RunnerStatus`), WHOSE it is (`RunnerKind` / `RunnerOwnerType` /
// `RunnerOwnerKind`) and the pair-shaped ownership those last two compose into.
// Split out of `../runner.ts`, which is now the barrel over this directory.

import { z } from 'zod';

export const RunnerStatus = {
  /** Minted enrollment token, runner hasn't dialed in yet. */
  Pending: 'pending',
  /** Enrolled and holding a live `FLEET_PRESENCE` KV key (TTL-refreshed). */
  Online: 'online',
  /** Presence reaper flipped it (#117) — its `FLEET_PRESENCE` key lapsed. */
  Offline: 'offline',
  /** Operator-killed credential; the auth hash no longer resolves. Terminal. */
  Revoked: 'revoked',
} as const;
export const RunnerStatusSchema = z.enum(RunnerStatus);
export type RunnerStatus = z.infer<typeof RunnerStatusSchema>;

export const RunnerKind = {
  /** User-attached VM that dials out to enroll (#110). Lives on the owner's box. */
  Byovm: 'byovm',
  /** Mothership-owned box in our managed pool (#115). We provision + run it. */
  Fleet: 'fleet',
} as const;
export const RunnerKindSchema = z.enum(RunnerKind);
export type RunnerKind = z.infer<typeof RunnerKindSchema>;

export const RunnerOwnerType = {
  /** Bound to one tenant (`accountId`) — every BYOVM runner is personal. */
  Personal: 'personal',
  /** Shared pool any tenant can be assigned onto (fleet only). */
  Shared: 'shared',
} as const;
export const RunnerOwnerTypeSchema = z.enum(RunnerOwnerType);
export type RunnerOwnerType = z.infer<typeof RunnerOwnerTypeSchema>;

/**
 * WHO a box belongs to for task-claim scoping — the ownership axis the assigner
 * matches a task against (the runner-ownership model). Distinct from the legacy
 * {@link RunnerOwnerType} (personal|shared, a billing/enrollment flag): this is
 * the dispatch-time scope of what work a box may pull.
 *
 *   - `org`    — PRIVATE to one org (`ownerId` = that `accounts.id`). Claims
 *     ONLY that org's tasks — the tenant-isolation default every enrolled box
 *     has always had, now made explicit and settable.
 *   - `user`   — PRIVATE to one person (`ownerId` = that `users.id`). Claims any
 *     task THAT USER dispatched, in ANY org they belong to — so a founder's own
 *     boxes follow them as they org-switch (serve their tesote AND developerz-ai
 *     work), and never touch another user's tasks.
 *
 * **Those two members are the whole vocabulary, and both are single-tenant at
 * the claim.** `org` resolves to one `accounts.id`; `user` resolves to its
 * owner's memberships and is fenced to the box's home account until an operator
 * attests container isolation. There is no third member and therefore no
 * cross-tenant fan-out to reach: `shared` — the cross-tenant pool kind for the
 * unbuilt enterprise extra (#133) — was refused at all three write seams from
 * #2296 and is RETIRED here, together with the `resolveCodingScope` branch that
 * handed an attested `shared` box every paying-tier account on the platform.
 * A refused-but-present value is the unrun branch this repo bans: it fires the
 * day someone re-adds the scope string that gates attestation. Measured before
 * removal (prod, 2026-09-02): `runners` held 13 `org`/online, 2 `org`/pending
 * and 12 `org`/revoked rows, and zero `shared` rows have ever been observed.
 * `runners_owner_kind_ck` (migration 0184) is the database's half of the same
 * refusal, and `schema.test.ts` holds the constraint to this enum by set
 * equality — so re-adding a member here fails the build rather than the INSERT.
 *
 * Text + Zod-at-read (D3 no-pgEnum stance, same as `kind` / `ownerType`).
 */
export const RunnerOwnerKind = {
  Org: 'org',
  User: 'user',
} as const;
export const RunnerOwnerKindSchema = z.enum(RunnerOwnerKind);
export type RunnerOwnerKind = z.infer<typeof RunnerOwnerKindSchema>;

/**
 * The ownership pair as a unit — `ownerKind` + its `ownerId` reference. `org`
 * points at an `accounts.id`, `user` at a `users.id`.
 */
export type RunnerOwnership = {
  ownerKind: RunnerOwnerKind;
  /**
   * `accounts.id` (org) | `users.id` (user). Nullable because the COLUMN is: a
   * pre-ownership row carries null and reads as `org` scoped to its
   * `account_id`. Never writable — {@link runnerOwnershipValid} refuses it.
   */
  ownerId: string | null;
};

/**
 * May this ownership pair be WRITTEN? Both kinds MUST name their owner — a
 * private box pinned to nobody could claim nothing, or — worse — everything.
 *
 * This is now a SHAPE gate only. It used to carry the tenancy half too, refusing
 * `shared` so the cross-tenant pool kind could not be minted by any account
 * holding `write:runners` over its own box. That member no longer exists, so the
 * refusal moved UP to the parse seam it belongs on: `RunnerOwnerKindSchema` is
 * what both write doors (`SetOwnerBodySchema` in `apps/api`, the MCP `box
 * set_owner` input) parse their `owner_kind` with, so `"shared"` is now a 400
 * naming the field rather than a value that reaches a predicate.
 *
 * Pure — no I/O, so every write layer shares it.
 */
export function runnerOwnershipValid(o: RunnerOwnership): boolean {
  return typeof o.ownerId === 'string' && o.ownerId.length > 0;
}
